import * as THREE from 'three'

/**
 * Clone geometry, scale it, and optionally flip or swap UVs.
 *
 * @param {THREE.BufferGeometry} geometry
 * @param {number} scaleX - geometry scale X
 * @param {number} scaleY - geometry scale Y
 * @param {number} scaleZ - geometry scale Z
 * @param {number} uvScaleX - UV repeat X
 * @param {number} uvScaleY - UV repeat Y
 * @param {Object} options - optional UV adjustments
 * @param {boolean} options.flipU - flip UV horizontally
 * @param {boolean} options.flipV - flip UV vertically
 * @param {boolean} options.swapUV - swap U and V
 * @returns {THREE.BufferGeometry}
 */
export function cloneAndScaleUV(
  geometry,
  scaleX = 1,
  scaleY = 1,
  scaleZ = 1,
  uvScaleX = 1,
  uvScaleY = 1,
  options = {}
) {
  const { flipU = false, flipV = false, swapUV = false } = options

  const clonedGeometry = geometry.clone()
  clonedGeometry.scale(scaleX, scaleY, scaleZ)

  const uvAttr = clonedGeometry.getAttribute('uv')
  if (uvAttr) {
    for (let i = 0; i < uvAttr.count; i++) {
      let u = uvAttr.getX(i)
      let v = uvAttr.getY(i)

      // Swap axes if needed
      if (swapUV) [u, v] = [v, u]

      // Flip if requested
      if (flipU) u = 1 - u
      if (flipV) v = 1 - v

      // Apply scaling
      u *= uvScaleX
      v *= uvScaleY

      uvAttr.setXY(i, u, v)
    }
    uvAttr.needsUpdate = true
  }

  const uv1Attr = geometry.getAttribute('uv1')
  if (uv1Attr) {
    clonedGeometry.deleteAttribute('uv1');
    const uv = clonedGeometry.getAttribute('uv');
    clonedGeometry.setAttribute('uv2', uv.clone());
  }

  return clonedGeometry
}

/**
 * Setup texture repeat (unchanged)
 */
export function setupTextureRepeat(tex, scaleX, scaleY) {
  if (!tex) return
  tex.wrapS = THREE.MirroredRepeatWrapping
  tex.wrapT = THREE.MirroredRepeatWrapping
  tex.repeat.set(scaleX, scaleY)
  tex.needsUpdate = true
}
